import { useState, useRef, useEffect, useCallback } from 'react';
import { uploadCompletionImage, submitManualCompletion } from '../../api/route_completions';
import logger from '../../utils/logger';

const MAX_DELIVERY_IMAGES = 5;
const MAX_PAPERWORK_IMAGES = 10;

const PodOffModal = ({ show, onClose, route, theme, onSuccess }) => {
    const [deliveryImages, setDeliveryImages] = useState([]);
    const [paperworkImages, setPaperworkImages] = useState([]);
    const [signedBy, setSignedBy] = useState('');
    const [completionNotes, setCompletionNotes] = useState('');
    const [driverComments, setDriverComments] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [uploadProgress, setUploadProgress] = useState('');

    // Signature canvas
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);

    const isLight = theme === 'light';

    // Reset form when modal opens
    useEffect(() => {
        if (show) {
            setDeliveryImages([]);
            setPaperworkImages([]);
            setSignedBy('');
            setCompletionNotes('');
            setDriverComments('');
            setError('');
            setUploadProgress('');
            setHasSignature(false);
            setSaving(false);
        }
    }, [show]);

    // Initialize canvas
    useEffect(() => {
        if (show && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
        }
    }, [show]);

    // --- Signature drawing handlers ---
    const getPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        if (e.touches) {
            return {
                x: (e.touches[0].clientX - rect.left) * scaleX,
                y: (e.touches[0].clientY - rect.top) * scaleY,
            };
        }
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY,
        };
    };

    const startDraw = useCallback((e) => {
        e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const pos = getPos(e);
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
        setIsDrawing(true);
    }, []);

    const draw = useCallback((e) => {
        if (!isDrawing) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const pos = getPos(e);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        setHasSignature(true);
    }, [isDrawing]);

    const stopDraw = useCallback((e) => {
        if (e) e.preventDefault();
        setIsDrawing(false);
    }, []);

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
    };

    const getSignatureDataUrl = () => {
        if (!hasSignature || !canvasRef.current) return null;
        return canvasRef.current.toDataURL('image/png');
    };

    // --- Image handlers ---
    const handleImageSelect = (type) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*,.pdf';
        input.multiple = true;
        input.onchange = (e) => {
            const files = Array.from(e.target.files);
            if (type === 'delivery') {
                const remaining = MAX_DELIVERY_IMAGES - deliveryImages.length;
                const toAdd = files.slice(0, remaining);
                setDeliveryImages(prev => [...prev, ...toAdd]);
            } else {
                const remaining = MAX_PAPERWORK_IMAGES - paperworkImages.length;
                const toAdd = files.slice(0, remaining);
                setPaperworkImages(prev => [...prev, ...toAdd]);
            }
        };
        input.click();
    };

    const removeImage = (type, index) => {
        if (type === 'delivery') {
            setDeliveryImages(prev => prev.filter((_, i) => i !== index));
        } else {
            setPaperworkImages(prev => prev.filter((_, i) => i !== index));
        }
    };

    // --- Submit ---
    const handleSubmit = async () => {
        setError('');

        if (!route?.route_uuid) {
            setError('No route selected.');
            return;
        }

        setSaving(true);

        try {
            // Upload delivery images
            const uploadedDeliveryPaths = [];
            for (let i = 0; i < deliveryImages.length; i++) {
                setUploadProgress(`Uploading delivery photo ${i + 1}/${deliveryImages.length}...`);
                const result = await uploadCompletionImage(deliveryImages[i]);
                if (!result.success || !result.path) {
                    throw new Error(`Failed to upload delivery photo ${i + 1}`);
                }
                uploadedDeliveryPaths.push(result.path);
            }

            // Upload paperwork images
            const uploadedPaperworkPaths = [];
            for (let i = 0; i < paperworkImages.length; i++) {
                setUploadProgress(`Uploading paperwork photo ${i + 1}/${paperworkImages.length}...`);
                const result = await uploadCompletionImage(paperworkImages[i]);
                if (!result.success || !result.path) {
                    throw new Error(`Failed to upload paperwork photo ${i + 1}`);
                }
                uploadedPaperworkPaths.push(result.path);
            }

            setUploadProgress('Submitting completion...');

            // Build signature data in the expected format
            let signatureData = null;
            const sigDataUrl = getSignatureDataUrl();
            if (sigDataUrl) {
                signatureData = JSON.stringify({
                    signature: sigDataUrl,
                    timestamp: new Date().toISOString(),
                });
            }

            const submissionData = {
                route_uuid: route.route_uuid,
                route_id: route.id || null,
                route_ref: route.route_ref || null,
                delivery_images: uploadedDeliveryPaths,
                paperwork_images: uploadedPaperworkPaths,
                customer_signature_data: signatureData,
                signed_by: signedBy || null,
                signed_at: hasSignature ? new Date().toISOString() : null,
                completion_notes: completionNotes || null,
                driver_comments: driverComments || null,
                latitude: 53.0185,
                longitude: -2.2026,
                geocoded_address: 'Yard (Manual POD)',
                location_accuracy: null,
            };

            const result = await submitManualCompletion(submissionData);

            if (result.success) {
                setUploadProgress('');
                onSuccess?.(route.route_uuid);
                onClose();
            } else {
                throw new Error(result.error || 'Failed to submit completion');
            }
        } catch (err) {
            logger.error('Pod Off submission error:', err);
            setError(err.message || 'An error occurred during submission.');
            setUploadProgress('');
        } finally {
            setSaving(false);
        }
    };

    if (!show) return null;

    const bgColor = isLight ? '#fff' : '#222';
    const textColor = isLight ? '#222' : '#fff';
    const borderColor = isLight ? '#ddd' : '#444';
    const inputBg = isLight ? '#f9f9f9' : '#333';

    return (
        <div
            className="modal-overlay"
            style={{
                position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center'
            }}
            onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
        >
            <div
                style={{
                    background: bgColor, color: textColor, borderRadius: 10,
                    border: '2px solid rgba(59, 130, 246, 0.8)',
                    padding: 28, minWidth: 520, maxWidth: 780, maxHeight: '90vh',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.4)', position: 'relative',
                    display: 'flex', flexDirection: 'column', gap: 0, overflowY: 'auto',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${borderColor}` }}>
                    <h3 style={{ margin: 0, fontSize: '1.3em', color: 'rgba(59, 130, 246, 0.9)' }}>
                        Manual Pod Off — {route?.route_ref || route?.route_uuid}
                    </h3>
                    <button
                        onClick={() => { if (!saving) onClose(); }}
                        style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: saving ? 'not-allowed' : 'pointer', color: isLight ? '#444' : '#ccc', lineHeight: 1 }}
                    >&times;</button>
                </div>

                {/* Route info */}
                {route && (
                    <div style={{ display: 'flex', gap: 24, marginBottom: 14, flexWrap: 'wrap', padding: '10px 14px', borderRadius: 6, background: isLight ? '#f5f5f5' : '#2a2a2a', border: `1px solid ${isLight ? '#e0e0e0' : '#3a3a3a'}` }}>
                        {route.job && <div><span style={{ opacity: 0.6, fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Job</span><br /><strong>{route.job}</strong></div>}
                        {route.driver && <div><span style={{ opacity: 0.6, fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Driver</span><br /><strong>{route.driver}</strong></div>}
                        {route.truck_reg && <div><span style={{ opacity: 0.6, fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Truck</span><br /><strong>{route.truck_reg}</strong></div>}
                        {route.trailer_reg && <div><span style={{ opacity: 0.6, fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Trailer</span><br /><strong>{route.trailer_reg}</strong></div>}
                        {(route.route || route.job_comments) && (
                            <div style={{ flexBasis: '100%', display: 'flex', gap: 24 }}>
                                {route.route && <div><span style={{ opacity: 0.6, fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Route</span><br /><strong>{route.route}</strong></div>}
                                {route.job_comments && <div><span style={{ opacity: 0.6, fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Job Comments</span><br /><span>{route.job_comments}</span></div>}
                            </div>
                        )}
                    </div>
                )}

                {/* Signed By */}
                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9em', opacity: 0.8 }}>Signed By (Customer Name)</label>
                    <input
                        type="text"
                        value={signedBy}
                        onChange={(e) => setSignedBy(e.target.value)}
                        placeholder="Enter customer name"
                        disabled={saving}
                        style={{
                            width: '100%', padding: '8px 12px', borderRadius: 6,
                            border: `1px solid ${borderColor}`, background: inputBg,
                            color: textColor, fontSize: '1em', boxSizing: 'border-box',
                        }}
                    />
                </div>

                {/* Signature Canvas */}
                <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <label style={{ fontSize: '0.9em', opacity: 0.8 }}>Customer Signature</label>
                        <button
                            onClick={clearSignature}
                            disabled={saving}
                            style={{
                                padding: '4px 12px', borderRadius: 4, border: `1px solid ${borderColor}`,
                                background: 'transparent', color: textColor, cursor: 'pointer', fontSize: '0.8em',
                            }}
                        >Clear</button>
                    </div>
                    <canvas
                        ref={canvasRef}
                        width={500}
                        height={180}
                        style={{
                            width: '100%', height: 180, borderRadius: 6,
                            border: `1px solid ${borderColor}`, background: '#fff',
                            cursor: 'crosshair', touchAction: 'none',
                        }}
                        onMouseDown={startDraw}
                        onMouseMove={draw}
                        onMouseUp={stopDraw}
                        onMouseLeave={stopDraw}
                        onTouchStart={startDraw}
                        onTouchMove={draw}
                        onTouchEnd={stopDraw}
                    />
                </div>

                {/* Delivery Images */}
                <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <label style={{ fontSize: '0.9em', opacity: 0.8 }}>Delivery Photos ({deliveryImages.length}/{MAX_DELIVERY_IMAGES})</label>
                        {deliveryImages.length < MAX_DELIVERY_IMAGES && (
                            <button
                                onClick={() => handleImageSelect('delivery')}
                                disabled={saving}
                                style={{
                                    padding: '4px 12px', borderRadius: 4, border: `1px solid rgba(59, 130, 246, 0.5)`,
                                    background: 'rgba(59, 130, 246, 0.1)', color: 'rgba(59, 130, 246, 0.9)',
                                    cursor: 'pointer', fontSize: '0.8em',
                                }}
                            >+ Add Files</button>
                        )}
                    </div>
                    {deliveryImages.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {deliveryImages.map((file, i) => (
                                <div key={i} style={{ position: 'relative' }}>
                                    {file.type === 'application/pdf' ? (
                                        <div style={{
                                            width: 80, height: 80, borderRadius: 6, border: `1px solid ${borderColor}`,
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                            background: 'rgba(239, 68, 68, 0.08)', fontSize: 11, textAlign: 'center', padding: 4,
                                        }}>
                                            <span style={{ fontSize: 24, marginBottom: 2 }}>📄</span>
                                            <span style={{ opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{file.name}</span>
                                        </div>
                                    ) : (
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt={`Delivery ${i + 1}`}
                                            style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: `1px solid ${borderColor}` }}
                                        />
                                    )}
                                    <button
                                        onClick={() => removeImage('delivery', i)}
                                        disabled={saving}
                                        style={{
                                            position: 'absolute', top: -6, right: -6, width: 20, height: 20,
                                            borderRadius: '50%', border: 'none', background: 'rgba(239, 68, 68, 0.9)',
                                            color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex',
                                            alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                                        }}
                                    >&times;</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Paperwork Images */}
                <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <label style={{ fontSize: '0.9em', opacity: 0.8 }}>Paperwork Photos ({paperworkImages.length}/{MAX_PAPERWORK_IMAGES})</label>
                        {paperworkImages.length < MAX_PAPERWORK_IMAGES && (
                            <button
                                onClick={() => handleImageSelect('paperwork')}
                                disabled={saving}
                                style={{
                                    padding: '4px 12px', borderRadius: 4, border: `1px solid rgba(59, 130, 246, 0.5)`,
                                    background: 'rgba(59, 130, 246, 0.1)', color: 'rgba(59, 130, 246, 0.9)',
                                    cursor: 'pointer', fontSize: '0.8em',
                                }}
                            >+ Add Files</button>
                        )}
                    </div>
                    {paperworkImages.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {paperworkImages.map((file, i) => (
                                <div key={i} style={{ position: 'relative' }}>
                                    {file.type === 'application/pdf' ? (
                                        <div style={{
                                            width: 80, height: 80, borderRadius: 6, border: `1px solid ${borderColor}`,
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                            background: 'rgba(239, 68, 68, 0.08)', fontSize: 11, textAlign: 'center', padding: 4,
                                        }}>
                                            <span style={{ fontSize: 24, marginBottom: 2 }}>📄</span>
                                            <span style={{ opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{file.name}</span>
                                        </div>
                                    ) : (
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt={`Paperwork ${i + 1}`}
                                            style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: `1px solid ${borderColor}` }}
                                        />
                                    )}
                                    <button
                                        onClick={() => removeImage('paperwork', i)}
                                        disabled={saving}
                                        style={{
                                            position: 'absolute', top: -6, right: -6, width: 20, height: 20,
                                            borderRadius: '50%', border: 'none', background: 'rgba(239, 68, 68, 0.9)',
                                            color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex',
                                            alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                                        }}
                                    >&times;</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Completion Notes */}
                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9em', opacity: 0.8 }}>Completion Notes</label>
                    <textarea
                        value={completionNotes}
                        onChange={(e) => setCompletionNotes(e.target.value)}
                        placeholder="Any notes about the delivery..."
                        disabled={saving}
                        rows={3}
                        style={{
                            width: '100%', padding: '8px 12px', borderRadius: 6,
                            border: `1px solid ${borderColor}`, background: inputBg,
                            color: textColor, fontSize: '1em', resize: 'vertical',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>

                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9em', opacity: 0.8 }}>Driver Comments</label>
                    <textarea
                        value={driverComments}
                        onChange={(e) => setDriverComments(e.target.value)}
                        placeholder="Any driver comments for this POD..."
                        disabled={saving}
                        rows={3}
                        style={{
                            width: '100%', padding: '8px 12px', borderRadius: 6,
                            border: `1px solid ${borderColor}`, background: inputBg,
                            color: textColor, fontSize: '1em', resize: 'vertical',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>

                {/* Error */}
                {error && (
                    <div style={{ padding: '8px 14px', marginBottom: 14, borderRadius: 6, background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)', color: 'rgba(239, 68, 68, 0.9)', fontSize: '0.9em' }}>
                        {error}
                    </div>
                )}

                {/* Upload progress */}
                {uploadProgress && (
                    <div style={{ padding: '8px 14px', marginBottom: 14, borderRadius: 6, background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', color: 'rgba(59, 130, 246, 0.9)', fontSize: '0.9em' }}>
                        {uploadProgress}
                    </div>
                )}

                {/* Footer */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8, borderTop: `1px solid ${borderColor}`, paddingTop: 14 }}>
                    <button
                        onClick={() => { if (!saving) onClose(); }}
                        disabled={saving}
                        style={{
                            padding: '8px 22px', borderRadius: 6, border: `1px solid ${borderColor}`,
                            background: 'transparent', color: textColor, cursor: saving ? 'not-allowed' : 'pointer', fontSize: '1em',
                        }}
                    >Cancel</button>
                    <button
                        onClick={handleSubmit}
                        disabled={saving || !signedBy.trim() || !hasSignature || deliveryImages.length === 0}
                        title={!signedBy.trim() ? 'Signed by name is required' : !hasSignature ? 'Signature is required' : deliveryImages.length === 0 ? 'At least 1 delivery photo is required' : ''}
                        style={{
                            padding: '8px 22px', borderRadius: 6, border: 'none',
                            background: (saving || !signedBy.trim() || !hasSignature || deliveryImages.length === 0) ? 'rgba(59, 130, 246, 0.35)' : 'rgba(59, 130, 246, 0.9)',
                            color: '#fff', cursor: (saving || !signedBy.trim() || !hasSignature || deliveryImages.length === 0) ? 'not-allowed' : 'pointer', fontSize: '1em', fontWeight: 'bold',
                        }}
                    >{saving ? 'Submitting...' : 'Submit Pod Off'}</button>
                </div>
            </div>
        </div>
    );
};

export default PodOffModal;
